import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { FlowCategory } from '../types/flowCategory';

type FlowCategoriesResponse = { data: FlowCategory[]; meta: { total: number } };
type FlowCategoryResponse = FlowCategory;

type DefaultError = unknown;

export const flowCategoryQueryKeys = {
  all: ['flow-categories'] as const,
  lists: () => [...flowCategoryQueryKeys.all, 'list'] as const,
  list: (params: Record<string, string>) =>
    [...flowCategoryQueryKeys.lists(), params] as const,
  details: () => [...flowCategoryQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...flowCategoryQueryKeys.details(), id] as const,
};

type UseFlowCategoriesParams = {
  searchParams?: Record<string, string>;
  enabled?: boolean;
};

export function useFlowCategories<
  TData = FlowCategoriesResponse,
  TError = DefaultError,
>(
  { searchParams = {}, enabled = true }: UseFlowCategoriesParams = {},
  options?: Omit<
    UseQueryOptions<FlowCategoriesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = flowCategoryQueryKeys.list(searchParams);

  return useQuery<FlowCategoriesResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getFlowCategories(searchParams),
    ...(options as any),
  });
}

type UseFlowCategoryParams = {
  id?: string;
  searchParams?: Record<string, string>;
  enabled?: boolean;
};

export function useFlowCategory<
  TData = FlowCategoryResponse,
  TError = DefaultError,
>(
  { id, searchParams = {}, enabled = true }: UseFlowCategoryParams = {},
  options?: Omit<
    UseQueryOptions<FlowCategoryResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = flowCategoryQueryKeys.detail(id);

  return useQuery<FlowCategoryResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => {
      if (!id) {
        throw new Error('FlowCategory ID is required');
      }
      return service.getFlowCategory(id, searchParams);
    },
    ...(options as any),
  });
}
